/**
 * Impressum Parser Service
 * Extracts business information from REAL scraped content (Firecrawl)
 */

import { openaiService } from './openaiService';
import { firecrawlService } from './firecrawlService';
import { getClient } from './geminiService';
import { placesService } from './placesService';

export interface BusinessInfo {
  businessName: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  phone?: string;
  email?: string;
  website?: string;
  taxId?: string;  // Steuernummer
  vatId?: string;  // USt-IdNr / VAT ID (e.g., DE123456789)
  commercialRegister?: string;  // Handelsregisternummer (e.g., HRB 12345)
  extractedFrom?: string;
  source?: 'impressum' | 'footer' | 'contact' | 'none';
  confidence?: number;
  warning?: string;
}

export const impressumParser = {
  /**
   * Find and extract business info by trying common Impressum URLs
   * Strategy: Try /impressum, /kontakt, etc. before falling back to main page
   */
  async findAndExtractBusinessInfo(baseUrl: string): Promise<BusinessInfo | null> {
    // Extensive list of German/European Impressum URLs
    const impressumUrls = [
      // German variants
      '/impressum',
      '/impressum.html',
      '/impressum/',
      '/de/impressum',
      '/de/impressum/',
      '/pages/impressum',
      '/page/impressum',
      // Imprint variants
      '/imprint',
      '/imprint.html',
      '/en/imprint',
      '/legal/imprint',
      // Legal pages
      '/legal',
      '/legal/',
      '/rechtliches',
      '/rechtliches/',
      '/legal-notice',
      '/legal-notice/',
      // About & Contact
      '/kontakt',
      '/kontakt.html',
      '/contact',
      '/about',
      '/about-us',
      '/ueber-uns',
      '/uber-uns',
      // Company info
      '/unternehmen',
      '/company',
      '/firma',
      // Footer links often used
      '/datenschutz', // Sometimes combined
      '/agb' // Sometimes has company info
    ];
    
    console.log('🔍 Searching for Impressum on common URLs...');

    for (const path of impressumUrls) {
      try {
        // Add significant delay to respect rate limits (30 req/min = 1 req every 2s)
        // Using 2500ms to be safe
        await new Promise(resolve => setTimeout(resolve, 2500));
        
        const fullUrl = new URL(path, baseUrl).href;
        console.log(`  Trying: ${fullUrl}`);
        
        const scraped = await firecrawlService.scrapeWebsite(fullUrl);
        
        // Verify this is actually an Impressum page
        const text = scraped.markdown.toLowerCase();
        if (text.includes('impressum') || 
            text.includes('handelsregister') ||
            text.includes('ust-idnr') ||
            text.includes('steuernummer') ||
            text.match(/\d{5}\s+[a-zäöüß]+/i)) { // PLZ + Stadt pattern
          
          console.log(`  ✅ Found Impressum at: ${fullUrl}`);
          
          // Extract business info from this page
          // For dedicated pages, use the markdown content directly as it's likely the main content
          const businessInfo = await this.parseFooterWithGemini(
            scraped.markdown.substring(0, 5000), // Use first 5000 chars of markdown
            fullUrl
          );
          
          if (businessInfo && (businessInfo.city || businessInfo.businessName)) {
            console.log(`  ✅ Extracted: ${businessInfo.businessName} in ${businessInfo.city}`);
            return {
              ...businessInfo,
              source: 'impressum'
            };
          }
        }
      } catch (error) {
        // Page doesn't exist or failed to load, try next
        console.log(`  ⚠️ Not found or failed: ${path}`);
        continue;
      }
    }
    
    console.log('  ⚠️ No dedicated Impressum found - trying main page...');
    
    // Fallback: Try to scrape the main page and extract from footer/meta
    try {
      const mainPage = await firecrawlService.scrapeWebsite(baseUrl);
      
      if (mainPage?.html) {
        // Try to extract from footer
        const footerText = this.findFooterInHTML(mainPage.html);
        if (footerText && footerText.length > 100) {
          const businessInfo = await this.parseFooterWithGemini(footerText, baseUrl);
          if (businessInfo && (businessInfo.city || businessInfo.businessName)) {
            console.log(`  ✅ Extracted from main page footer: ${businessInfo.businessName}`);
            return {
              ...businessInfo,
              source: 'footer'
            };
          }
        }
        
        // Last resort: Extract company name from meta tags
        const metaName = this.extractCompanyNameFromMeta(mainPage.html, baseUrl);
        if (metaName) {
          console.log(`  ✅ Extracted company name from meta: ${metaName}`);
          return {
            businessName: metaName,
            street: '',
            city: '',
            postalCode: '',
            country: '',
            website: baseUrl,
            source: 'footer',
            confidence: 40,
            warning: 'Only company name found from meta tags.'
          };
        }
      }
    } catch (error) {
      console.log('  ⚠️ Could not scrape main page for fallback');
    }
    
    return null;
  },

  /**
   * Extract business information from REAL scraped HTML (Firecrawl)
   * Searches Footer/Impressum sections in actual HTML
   */
  async extractBusinessInfoFromScrapedContent(html: string, markdown: string, url: string): Promise<BusinessInfo | null> {
    try {
      console.log('🔍 Extracting business info from REAL HTML...');
      
      // STEP 1: Find Footer/Impressum in REAL HTML
      const footerText = this.findFooterInHTML(html);
      
      if (!footerText || footerText.length < 50) {
        console.warn('⚠️ No Footer/Impressum section found');
        // Try to at least get company name from meta tags
        const fallbackName = this.extractCompanyNameFromMeta(html, url);
        if (fallbackName) {
          console.log(`✅ Fallback: Extracted company name from meta: "${fallbackName}"`);
          return {
            ...this.getEmptyBusinessInfo(url),
            businessName: fallbackName,
            source: 'footer',
            confidence: 40,
            warning: 'Only company name found (from meta tags). Address not available.'
          };
        }
        return this.getEmptyBusinessInfo(url);
      }
      
      console.log(`✅ Found Footer section (${footerText.length} chars)`);
      
      // STEP 2: Extract business data with Gemini
      const businessInfo = await this.parseFooterWithGemini(footerText, url);
      
      // STEP 3: If no business name found, try meta tags as fallback
      if (!businessInfo.businessName || businessInfo.businessName.trim() === '') {
        const metaName = this.extractCompanyNameFromMeta(html, url);
        if (metaName) {
          console.log(`✅ Fallback: Using company name from meta: "${metaName}"`);
          businessInfo.businessName = metaName;
          businessInfo.confidence = Math.min(businessInfo.confidence || 50, 70);
        }
      }
      
      return businessInfo;
      
    } catch (error) {
      console.error('Error extracting business info:', error);
      return this.getEmptyBusinessInfo(url);
    }
  },

  /**
   * Find Footer/Impressum in HTML - Enhanced detection
   */
  findFooterInHTML(html: string): string | null {
    const lowerHtml = html.toLowerCase();
    let bestMatch = '';
    
    // Method 1: Look for dedicated Impressum section (most reliable)
    // Pattern: section/div with id/class containing "impressum"
    const impressumSectionRegex = /<(?:div|section|article)[^>]*(?:id|class)=["'][^"']*impressum[^"']*["'][^>]*>([\s\S]*?)<\/(?:div|section|article)>/gi;
    const impressumSections = html.match(impressumSectionRegex);
    if (impressumSections && impressumSections.length > 0) {
      const section = impressumSections[0];
      const cleaned = section.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      if (cleaned.length > 100) {
        console.log('  → Found dedicated Impressum section');
        return cleaned;
      }
    }
    
    // Method 2: Look for <footer> tags
    const footerRegex = /<footer[^>]*>([\s\S]*?)<\/footer>/gi;
    const footerMatches = html.match(footerRegex);
    
    if (footerMatches && footerMatches.length > 0) {
      const longestFooter = footerMatches.reduce((a, b) => a.length > b.length ? a : b);
      bestMatch = longestFooter.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      console.log(`  → Found footer tag (${bestMatch.length} chars)`);
    }
    
    // Method 3: Search for "Impressum" keyword with extended context
    const impressumKeywords = ['impressum', 'angaben gemäß', 'verantwortlich für', 'handelsregister', 'amtsgericht', 'ust-idnr', 'geschäftsführer'];
    
    for (const keyword of impressumKeywords) {
      const keywordIndex = lowerHtml.indexOf(keyword);
      
      if (keywordIndex !== -1) {
        // Extract larger context for Impressum content
        const start = Math.max(0, keywordIndex - 500);
        const end = Math.min(html.length, keywordIndex + 2000);
        const section = html.substring(start, end);
        const cleaned = section.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        
        // Check if this section has more business info than current best
        const hasAddress = /\d{4,5}\s+[A-Za-zäöüÄÖÜß]+/.test(cleaned); // PLZ + Stadt
        const hasCompany = /(GmbH|AG|UG|e\.?K\.|OHG|KG|GbR)/i.test(cleaned);
        
        if ((hasAddress || hasCompany) && cleaned.length > bestMatch.length) {
          bestMatch = cleaned;
          console.log(`  → Found "${keyword}" keyword section (${cleaned.length} chars)`);
        }
      }
    }
    
    // Method 4: Look for contact section
    if (!bestMatch || bestMatch.length < 100) {
      const contactSectionRegex = /<(?:div|section|article)[^>]*(?:id|class)=["'][^"']*(?:contact|kontakt|footer)[^"']*["'][^>]*>([\s\S]*?)<\/(?:div|section|article)>/gi;
      const contactSections = html.match(contactSectionRegex);
      if (contactSections && contactSections.length > 0) {
        const section = contactSections.reduce((a, b) => a.length > b.length ? a : b);
        const cleaned = section.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        if (cleaned.length > bestMatch.length) {
          bestMatch = cleaned;
          console.log(`  → Found contact/footer section (${cleaned.length} chars)`);
        }
      }
    }
    
    // Method 5: Fallback - last 3000 chars of HTML
    if (!bestMatch || bestMatch.length < 100) {
      const lastSection = html.substring(Math.max(0, html.length - 3000));
      bestMatch = lastSection.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      console.log(`  → Using last section fallback (${bestMatch.length} chars)`);
    }
    
    return bestMatch.length > 0 ? bestMatch : null;
  },

  /**
   * Parse Footer text with Gemini to extract address AND tax IDs
   * Enhanced with better prompting for company name and address extraction
   */
  async parseFooterWithGemini(footerText: string, url: string): Promise<BusinessInfo> {
    try {
      // Extract domain for context
      const domain = new URL(url).hostname.replace('www.', '');
      
      const client = getClient();
      const response = await client.models.generateContent({
        model: "gemini-2.5-flash",
        contents: [{
          parts: [{
            text: `You are an expert at extracting business information from German/Austrian/Swiss website Impressum pages.

WEBSITE: ${url}
DOMAIN: ${domain}

TEXT TO ANALYZE:
---
${footerText}
---

EXTRACTION RULES:

1. **COMPANY NAME** (businessName):
   - Look for: "Firma:", "Unternehmen:", company name before "GmbH", "AG", "UG", "e.K.", "OHG", "KG", "GbR"
   - Often appears at the TOP of Impressum
   - If you see "Inhaber:" or "Geschäftsführer:", the name BEFORE that is often the company
   - Pattern: "[Company Name] GmbH" or "[Company Name] AG" etc.
   - DO NOT confuse with person names (Geschäftsführer, Inhaber)
   - PREFER legal entity names (ending with GmbH, AG, etc.) over person names

2. **STREET ADDRESS** (street):
   - German format: "Musterstraße 123" or "Muster Str. 123"
   - May include building/floor: "Musterstraße 123, 4. OG"
   - Look for: "Straße", "Str.", "Weg", "Platz", "Allee", "Ring"

3. **POSTAL CODE** (postalCode):
   - German: 5 digits (e.g., "80331")
   - Austrian: 4 digits (e.g., "1010")
   - Swiss: 4 digits (e.g., "8001")
   - Always appears BEFORE the city name

4. **CITY** (city):
   - Directly after postal code: "80331 München"
   - May include district: "München-Schwabing"

5. **COUNTRY** (country):
   - Often NOT explicitly stated if German company
   - Infer from postal code format:
     * 5 digits = Deutschland
     * 4 digits starting with 1-9 = Österreich or Schweiz
   - Look for: "Deutschland", "Germany", "Österreich", "Austria", "Schweiz", "Switzerland"

6. **PHONE** (phone):
   - Formats: "+49 (0)...", "0049...", "Tel:", "Telefon:", "Fon:"
   - Include full number with area code

7. **EMAIL** (email):
   - Look for "@" symbol or "E-Mail:", "Mail:"

8. **TAX IDs**:
   - Steuernummer (taxId): "Steuernummer:", "St.-Nr.:", format "123/456/78901"
   - USt-IdNr (vatId): "DE123456789", "ATU12345678", "CHE-123.456.789"

9. **COMMERCIAL REGISTER** (commercialRegister):
   - "HRB 12345", "HRA 12345", "Amtsgericht [City] HRB [Number]"
   - "Handelsregister:", "Registergericht:"

IMPORTANT:
- Return the FULL legal company name (e.g., "AIDELINE GmbH" not just "AIDELINE")
- Combine street + house number in "street" field
- Use "Deutschland" for German addresses (not "Germany")
- Confidence should reflect data completeness (100 = all found, 50 = only partial)

Return ONLY valid JSON:
{
  "businessName": "",
  "street": "",
  "postalCode": "",
  "city": "",
  "country": "",
  "phone": "",
  "email": "",
  "taxId": "",
  "vatId": "",
  "commercialRegister": "",
  "confidence": 0
}

Use empty string "" if a field is not found.`
          }]
        }],
        generationConfig: {
          temperature: 0.05, // Lower temperature for more precise extraction
          maxOutputTokens: 600,
          responseMimeType: "application/json"
        }
      });
      
      const responseText = response.text || response.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
      const cleanedText = responseText.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      const data = JSON.parse(cleanedText);
      
      return {
        businessName: data.businessName || '',
        street: data.street || '',
        city: data.city || '',
        postalCode: data.postalCode || '',
        country: data.country || '',
        phone: data.phone,
        email: data.email,
        taxId: data.taxId,  // NEW: Steuernummer
        vatId: data.vatId,  // NEW: USt-IdNr
        commercialRegister: data.commercialRegister,  // NEW: Handelsregister
        website: url,
        extractedFrom: 'footer',
        source: data.city ? 'impressum' : 'none',
        confidence: data.confidence || 80
      };
      
    } catch (error) {
      console.error('Error parsing footer:', error);
      return this.getEmptyBusinessInfo(url);
    }
  },

  /**
   * Extract company name from HTML metadata as fallback
   */
  extractCompanyNameFromMeta(html: string, url: string): string {
    try {
      // 1. Try og:site_name (most reliable for company name)
      const ogSiteNameMatch = html.match(/<meta\s+property=["']og:site_name["']\s+content=["']([^"']+)["']/i);
      if (ogSiteNameMatch?.[1]) {
        return ogSiteNameMatch[1].trim();
      }
      
      // 2. Try application-name
      const appNameMatch = html.match(/<meta\s+name=["']application-name["']\s+content=["']([^"']+)["']/i);
      if (appNameMatch?.[1]) {
        return appNameMatch[1].trim();
      }
      
      // 3. Try title tag (often has company name)
      const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i);
      if (titleMatch?.[1]) {
        // Clean up title - often format: "Page Name | Company Name" or "Company Name - Page"
        const title = titleMatch[1].trim();
        // Check for separator patterns
        const separators = [' | ', ' - ', ' – ', ' — ', ' :: '];
        for (const sep of separators) {
          if (title.includes(sep)) {
            const parts = title.split(sep);
            // Usually company name is last part
            const lastPart = parts[parts.length - 1].trim();
            // Or first part if it looks more like a company name
            const firstPart = parts[0].trim();
            
            // Prefer part that looks like a company (GmbH, AG, etc.)
            if (lastPart.match(/(GmbH|AG|UG|e\.?K\.?|OHG|KG|GbR|Inc|Ltd|LLC|Corp)/i)) {
              return lastPart;
            }
            if (firstPart.match(/(GmbH|AG|UG|e\.?K\.?|OHG|KG|GbR|Inc|Ltd|LLC|Corp)/i)) {
              return firstPart;
            }
            // Otherwise use the shorter part (usually company name)
            return firstPart.length <= lastPart.length ? firstPart : lastPart;
          }
        }
        // No separator, use full title if short enough
        if (title.length < 50) {
          return title;
        }
      }
      
      // 4. Last resort: Extract from domain
      const domain = new URL(url).hostname.replace('www.', '');
      const domainName = domain.split('.')[0];
      // Capitalize first letter
      return domainName.charAt(0).toUpperCase() + domainName.slice(1);
      
    } catch (error) {
      console.error('Error extracting company name from meta:', error);
      return '';
    }
  },

  /**
   * Empty business info
   */
  getEmptyBusinessInfo(url: string): BusinessInfo {
    return {
      businessName: '',
      street: '',
      city: '',
      postalCode: '',
      country: '',
      website: url,
      extractedFrom: 'none',
      source: 'none',
      confidence: 0,
      warning: 'Business location not found in Footer/Impressum.'
    };
  },

  /**
   * Legacy method (fallback without Firecrawl)
   */
  async extractBusinessInfo(url: string): Promise<BusinessInfo | null> {
    try {
      const businessInfo = await openaiService.extractBusinessInfoFromUrl(url);
      
      if (businessInfo) {
        const hasData = businessInfo.businessName || businessInfo.city || businessInfo.country;
        const confidence = businessInfo.confidence || 0;
        
        if (!hasData || confidence < 50) {
          return this.getEmptyBusinessInfo(url);
        }
        
        return {
          ...businessInfo,
          website: url,
          extractedFrom: 'openai',
          warning: (!businessInfo.country || !businessInfo.city) 
            ? 'Incomplete location data.' 
            : undefined
        };
      }
      
      return this.getEmptyBusinessInfo(url);
      
    } catch (error) {
      console.error('Error extracting business info:', error);
      return null;
    }
  },

  /**
   * Get country for location targeting
   * Default: Germany (DACH focus)
   */
  getTargetLocation(businessInfo: BusinessInfo): string {
    const countryMap: Record<string, string> = {
      'Germany': 'Germany',
      'Deutschland': 'Germany',
      'United States': 'United States',
      'USA': 'United States',
      'Austria': 'Austria',
      'Österreich': 'Austria',
      'Switzerland': 'Switzerland',
      'Schweiz': 'Switzerland'
    };

    return countryMap[businessInfo.country] || businessInfo.country || 'Germany'; // Changed from 'United States'
  },

  /**
   * Find all business locations (multi-location support)
   * Detects multiple locations from impressum (e.g., branch networks)
   */
  async findAllLocations(baseUrl: string): Promise<Array<{
    address: string;
    city: string;
    coordinates: { lat: number; lng: number };
    source: 'impressum' | 'google_places';
  }>> {
    try {
      console.log('🔍 Searching for multiple business locations...');

      // First, get the primary location from impressum
      const primaryInfo = await this.findAndExtractBusinessInfo(baseUrl);
      
      if (!primaryInfo?.city) {
        console.log('  ℹ️ No primary location found');
        return [];
      }
      
      const locations: Array<{
        address: string;
        city: string;
        coordinates: { lat: number; lng: number };
        source: 'impressum' | 'google_places';
      }> = [];
      
      // Geocode primary location
      try {
        const primaryAddress = `${primaryInfo.street || ''}, ${primaryInfo.postalCode || ''} ${primaryInfo.city}, ${primaryInfo.country || 'Deutschland'}`.trim();
        const coords = await placesService.geocodeAddress(primaryAddress);
        
        if (coords) {
          locations.push({
            address: primaryAddress,
            city: primaryInfo.city,
            coordinates: coords,
            source: 'impressum'
          });
          console.log(`  ✅ Primary location: ${primaryInfo.city}`);
        }
      } catch (error) {
        console.warn('  ⚠️ Failed to geocode primary location:', error);
      }
      
      // Try to find additional locations from impressum page
      try {
        const impressumUrls = ['/impressum', '/standorte', '/filialen', '/locations', '/branches'];
        
        for (const path of impressumUrls) {
          try {
            const fullUrl = new URL(path, baseUrl).href;
            const scraped = await firecrawlService.scrapeWebsite(fullUrl);
            
            // Look for additional location patterns in markdown
            const additionalLocations = await this.extractAdditionalLocations(
              scraped.markdown,
              primaryInfo.country || 'Deutschland'
            );
            
            // Geocode additional locations
            for (const loc of additionalLocations) {
              try {
                const coords = await placesService.geocodeAddress(loc.address);
                if (coords && !locations.some(l => l.city === loc.city)) {
                  locations.push({
                    ...loc,
                    coordinates: coords,
                    source: 'impressum'
                  });
                  console.log(`  ✅ Additional location: ${loc.city}`);
                }
              } catch (error) {
                console.warn(`  ⚠️ Failed to geocode ${loc.city}`);
              }
            }
            
            if (additionalLocations.length > 0) {
              break; // Found additional locations, no need to check other URLs
            }
          } catch (error) {
            continue; // Try next URL
          }
        }
      } catch (error) {
        console.warn('  ⚠️ Failed to find additional locations:', error);
      }
      
      console.log(`  ✅ Found ${locations.length} total location(s)`);
      return locations;
      
    } catch (error) {
      console.error('❌ Multi-location detection failed:', error);
      return [];
    }
  },

  /**
   * Extract additional locations from impressum text using patterns
   */
  async extractAdditionalLocations(
    markdown: string,
    defaultCountry: string
  ): Promise<Array<{ address: string; city: string }>> {
    const locations: Array<{ address: string; city: string }> = [];
    
    // Look for German address patterns (PLZ + City)
    // Pattern: 5 digits + space + city name
    const addressPattern = /(\d{5})\s+([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)?)/g;
    const matches = [...markdown.matchAll(addressPattern)];
    
    for (const match of matches) {
      const postalCode = match[1];
      const city = match[2];
      
      // Skip if it looks like a date or other number
      if (parseInt(postalCode) < 1000 || parseInt(postalCode) > 99999) {
        continue;
      }
      
      locations.push({
        address: `${postalCode} ${city}, ${defaultCountry}`,
        city
      });
    }
    
    // Deduplicate by city
    const unique = locations.filter((loc, index, self) => 
      index === self.findIndex(l => l.city === loc.city)
    );
    
    return unique;
  }
};
